import type { Question } from '@api/instant_survey/types';

import { getQuestionDisplayIndexById } from '../../../utilities/helpers';

interface PipingMentionMapping {
  newCanonicalQuestionNumber?: number;
  newDisplayQuestionNumber?: QuestionNumber;
  oldCanonicalQuestionNumber?: number;
  oldDisplayQuestionNumber?: QuestionNumber;
}

type QuestionNumber = null | number | string;

interface ReplacePipingMentionReferenceOptions extends PipingMentionMapping {
  value?: string;
}

interface ReplacePipingMentionReferencesOptions {
  mappings?: PipingMentionMapping[];
  value?: string;
}

interface ResyncCanonicalPipingReferencesOptions {
  originalQuestions?: Question[];
  updatedQuestions?: Question[];
}

/**
 * Updates one piping mention while preserving the two numbering systems:
 * canonical data attributes for Preview resolution and display labels for Builder text.
 */
export const replacePipingMentionReference = ({
  newCanonicalQuestionNumber,
  newDisplayQuestionNumber,
  oldCanonicalQuestionNumber,
  oldDisplayQuestionNumber,
  value,
}: ReplacePipingMentionReferenceOptions) => {
  if (typeof value !== 'string') return value;

  let nextValue = value;

  if (Number.isFinite(oldCanonicalQuestionNumber) && Number.isFinite(newCanonicalQuestionNumber)) {
    nextValue = nextValue.replace(
      // eslint-disable-next-line security/detect-unsafe-regex
      /(data-mention-id="answer from q)(\d+)(\.\d+)?(")/g,
      (match, prefix, questionNumber, subQuestionSuffix = '', suffix) =>
        Number(questionNumber) === Number(oldCanonicalQuestionNumber)
          ? `${prefix}${newCanonicalQuestionNumber}${subQuestionSuffix}${suffix}`
          : match
    );
  }

  if (Number.isFinite(oldDisplayQuestionNumber) && Number.isFinite(newDisplayQuestionNumber)) {
    nextValue = nextValue.replace(
      // eslint-disable-next-line security/detect-unsafe-regex
      /\{answer from q(\d+)(\.\d+)?\}/g,
      (match, questionNumber, subQuestionSuffix = '') =>
        Number(questionNumber) === Number(oldDisplayQuestionNumber)
          ? `{answer from q${newDisplayQuestionNumber}${subQuestionSuffix}}`
          : match
    );
  }

  return nextValue;
};

export const getPipingQuestionNumbersById = (
  questionsList: Question[] = []
): Record<Question['id'], number> => {
  let questionNumber = 1;

  return questionsList.reduce<Record<Question['id'], number>>((acc, question) => {
    if (question._delete) return acc;

    acc[question.id] = questionNumber;
    questionNumber += 1;
    return acc;
  }, {});
};

/** Updates canonical piping ids in rich text and display question numbers in Builder text. */
export const replacePipingMentionReferences = ({
  mappings = [],
  value,
}: ReplacePipingMentionReferencesOptions) => {
  if (typeof value !== 'string' || mappings.length === 0) return value;

  const canonicalQuestionNumberMap = new Map<number, number>();
  const getRangeStart = (questionNumber: QuestionNumber) => String(questionNumber).split('-')[0];
  const displayQuestionNumberMap = new Map<string, string>();

  mappings.forEach(
    ({
      newCanonicalQuestionNumber,
      newDisplayQuestionNumber,
      oldCanonicalQuestionNumber,
      oldDisplayQuestionNumber,
    }) => {
      if (
        oldDisplayQuestionNumber !== undefined &&
        oldDisplayQuestionNumber !== null &&
        newDisplayQuestionNumber !== undefined &&
        newDisplayQuestionNumber !== null
      ) {
        displayQuestionNumberMap.set(
          getRangeStart(oldDisplayQuestionNumber),
          getRangeStart(newDisplayQuestionNumber)
        );
      }

      if (
        Number.isFinite(oldCanonicalQuestionNumber) &&
        Number.isFinite(newCanonicalQuestionNumber)
      ) {
        canonicalQuestionNumberMap.set(
          Number(oldCanonicalQuestionNumber),
          Number(newCanonicalQuestionNumber)
        );
      }
    }
  );

  let nextValue = value;

  if (canonicalQuestionNumberMap.size > 0) {
    nextValue = nextValue.replace(
      // eslint-disable-next-line security/detect-unsafe-regex
      /(data-mention-id="answer from q)(\d+)(\.\d+)?(")/g,
      (match, prefix, questionNumber, subQuestionSuffix = '', suffix) => {
        const mappedQuestionNumber = canonicalQuestionNumberMap.get(Number(questionNumber));

        return Number.isFinite(mappedQuestionNumber)
          ? `${prefix}${mappedQuestionNumber}${subQuestionSuffix}${suffix}`
          : match;
      }
    );
  }

  if (displayQuestionNumberMap.size > 0) {
    nextValue = nextValue.replace(
      // eslint-disable-next-line security/detect-unsafe-regex
      /\{answer from q(\d+)(?:-(\d+))?(\.\d+)?\}/g,
      (match, startQuestionNumber, endQuestionNumber, subQuestionSuffix = '') => {
        const mappedStartQuestionNumber = displayQuestionNumberMap.get(String(startQuestionNumber));
        if (!mappedStartQuestionNumber) return match;

        const mappedRange = endQuestionNumber
          ? `-${
              Number(endQuestionNumber) +
              (Number(mappedStartQuestionNumber) - Number(startQuestionNumber))
            }`
          : '';

        return `{answer from q${mappedStartQuestionNumber}${mappedRange}${subQuestionSuffix}}`;
      }
    );
  }

  return nextValue;
};

/**
 * Rebuilds canonical piping ids from the display label saved inside each mention. The display
 * label excludes screeners, so it remains stable when screeners are inserted or removed.
 */
const replaceCanonicalPipingReferencesFromDisplay = ({
  canonicalQuestionNumberByDisplayNumber,
  value,
}: {
  canonicalQuestionNumberByDisplayNumber: Map<number, number>;
  value?: string;
}) => {
  if (typeof value !== 'string' || canonicalQuestionNumberByDisplayNumber.size === 0) return value;

  return value.replace(
    // eslint-disable-next-line security/detect-unsafe-regex
    /(data-mention-id="answer from q)(\d+)(\.\d+)?(")/g,
    (match, prefix, _questionNumber, subQuestionSuffix = '', suffix, offset, source) => {
      const mentionEnd = source.indexOf('</span>', offset + match.length);
      if (mentionEnd === -1) return match;

      const mentionContent = source.slice(offset + match.length, mentionEnd);
      // eslint-disable-next-line security/detect-unsafe-regex
      const displayQuestionNumber = /\{answer from q(\d+)(?:-\d+)?(?:\.\d+)?\}/.exec(
        mentionContent
      )?.[1];
      const canonicalQuestionNumber = canonicalQuestionNumberByDisplayNumber.get(
        Number(displayQuestionNumber)
      );

      return Number.isFinite(canonicalQuestionNumber)
        ? `${prefix}${canonicalQuestionNumber}${subQuestionSuffix}${suffix}`
        : match;
    }
  );
};

/**
 * Resyncs hidden canonical piping ids after screeners change the persisted question order.
 * Visible question numbers are intentionally unchanged because screeners have no display cost.
 */
export const resyncCanonicalPipingReferences = ({
  originalQuestions = [],
  updatedQuestions = [],
}: ResyncCanonicalPipingReferencesOptions): Question[] => {
  const originalPipingQuestionNumbers = getPipingQuestionNumbersById(originalQuestions);
  const updatedPipingQuestionNumbers = getPipingQuestionNumbersById(updatedQuestions);
  const updatedDisplayQuestionNumbers = getQuestionDisplayIndexById(updatedQuestions) as Record<
    Question['id'],
    string
  >;
  const canonicalQuestionNumberByDisplayNumber = new Map<number, number>();

  updatedQuestions.forEach(({ _delete, id, isScreener }) => {
    const displayQuestionNumber = Number(String(updatedDisplayQuestionNumbers[id]).split('-')[0]);
    const canonicalQuestionNumber = updatedPipingQuestionNumbers[id];

    if (
      !_delete &&
      !isScreener &&
      Number.isFinite(displayQuestionNumber) &&
      Number.isFinite(canonicalQuestionNumber)
    ) {
      canonicalQuestionNumberByDisplayNumber.set(displayQuestionNumber, canonicalQuestionNumber);
    }
  });

  const mappings = originalQuestions
    .filter(({ _delete, isScreener }) => !_delete && !isScreener)
    .map(({ id }) => ({
      newCanonicalQuestionNumber: updatedPipingQuestionNumbers[id],
      oldCanonicalQuestionNumber: originalPipingQuestionNumbers[id],
    }))
    .filter(
      ({ newCanonicalQuestionNumber, oldCanonicalQuestionNumber }) =>
        Number.isFinite(newCanonicalQuestionNumber) &&
        newCanonicalQuestionNumber !== oldCanonicalQuestionNumber
    );

  return updatedQuestions.map((question) => {
    const resyncValue = (value?: string) =>
      replaceCanonicalPipingReferencesFromDisplay({
        canonicalQuestionNumberByDisplayNumber,
        value: replacePipingMentionReferences({ mappings, value }),
      });
    const nextRichText = resyncValue(question.richText);
    const nextText = resyncValue(question.text) ?? question.text;
    let hasAnswerChanges = false;
    const nextAnswers = (question.answers || []).map((answer) => {
      const nextAnswerRichText = resyncValue(answer.richText);
      const nextAnswerText = resyncValue(answer.text) ?? answer.text;

      if (nextAnswerRichText === answer.richText && nextAnswerText === answer.text) return answer;

      hasAnswerChanges = true;
      return {
        ...answer,
        richText: nextAnswerRichText,
        text: nextAnswerText,
      };
    });

    if (nextRichText === question.richText && nextText === question.text && !hasAnswerChanges) {
      return question;
    }

    return {
      ...question,
      answers: nextAnswers,
      richText: nextRichText,
      text: nextText,
    };
  });
};
